/**
 * A failed solution open must not be sent again for the same id.
 *
 * `ps-data-table` emits `row-selection-change` from `handleTableStateChange`
 * on every table-state change while row selection is enabled. The solutions
 * table builds a new `data` array inside its context selector, so that emit
 * repeats the row that is still selected. `selectBot` is handled in `idle`,
 * and `botFlowError` returns there after 100ms, which calls `getBotById`
 * again. `acceptSolutionOpen` is the `selectBot` cond. `force` is checked
 * before the already-open name comparison, so Try again still fetches when
 * that display name is already selected. `solutionSelectEvent` uses the same
 * id gate for the table emit.
 */

export type SolutionOpenFailure = {
  solutionId: string;
  displayName: string;
};

export type SolutionListRow = {
  id?: string;
  displayName?: string | null;
  name?: string | null;
};

export type DeleteSolutionTarget = {
  id: string;
  displayName: string;
  solutionName: string | undefined;
};

export function acceptSolutionOpen(input: {
  selectedDisplayName: string | undefined;
  requestedName: string;
  requestedId: string;
  openFailureId: string | null | undefined;
  force?: boolean;
}): boolean {
  if (input.force) {
    return true;
  }

  return (
    input.selectedDisplayName !== input.requestedName &&
    shouldDispatchSolutionSelect({
      requestedId: input.requestedId,
      openFailureId: input.openFailureId,
      force: input.force,
    })
  );
}

export function shouldDispatchSolutionSelect(input: {
  requestedId: string;
  openFailureId: string | null | undefined;
  force?: boolean;
}): boolean {
  if (input.force) {
    return true;
  }

  return input.openFailureId !== input.requestedId;
}

export function solutionSelectEvent(input: {
  requestedId: string | undefined;
  requestedName: string | undefined;
  openFailureId: string | null | undefined;
  force?: boolean;
}): { type: 'selectBot'; id: string; name: string; force?: boolean } | null {
  if (!input.requestedId || !input.requestedName) {
    return null;
  }

  if (
    !shouldDispatchSolutionSelect({
      requestedId: input.requestedId,
      openFailureId: input.openFailureId,
      force: input.force,
    })
  ) {
    return null;
  }

  return {
    type: 'selectBot',
    id: input.requestedId,
    name: input.requestedName,
    ...(input.force ? { force: true as const } : {}),
  };
}

export function failureFromOpenAttempt(
  attempt: SolutionOpenFailure | null | undefined
): SolutionOpenFailure | null {
  if (!attempt?.solutionId || !attempt.displayName) {
    return null;
  }

  return {
    solutionId: attempt.solutionId,
    displayName: attempt.displayName,
  };
}

export function failureFromLoadedSolution(input: {
  solutions: readonly SolutionListRow[] | undefined;
  botName: string | undefined;
  botIdURLParam: string | undefined;
}): SolutionOpenFailure | null {
  const byId = input.botIdURLParam
    ? input.solutions?.find((solution) => solution.id === input.botIdURLParam)
    : undefined;
  const row =
    byId ??
    (input.botName
      ? input.solutions?.find((solution) => solution.name === input.botName)
      : undefined);

  const displayName = row?.displayName || row?.name;
  if (!row?.id || !displayName) {
    return null;
  }

  return { solutionId: row.id, displayName };
}

/**
 * Failure record kept on the `selectBot` that is about to fetch.
 * The same id keeps it, so a retry still has a delete target. A different id
 * drops it.
 */
export function failureHeldForSelect(input: {
  current: SolutionOpenFailure | null | undefined;
  requestedId: string;
}): SolutionOpenFailure | null {
  if (input.current && input.current.solutionId === input.requestedId) {
    return input.current;
  }

  return null;
}

/**
 * True when `selectBot` names the id already stored on the failure and
 * `force` is absent. The idle transition that uses this cond assigns the
 * dismiss flag back to false and has no `target`, so it does not enter
 * `#setSelectedSolution`.
 */
export function isLatchedFailureReveal(input: {
  openFailureId: string | null | undefined;
  requestedId: string;
  force?: boolean;
}): boolean {
  if (input.force) {
    return false;
  }

  return !!input.openFailureId && input.openFailureId === input.requestedId;
}

/**
 * The solutions table re-emits the row that is still selected. Only a
 * selection that just moved onto the failed id asks the dialog to show.
 */
export function shouldRevealFailedSelection(input: {
  previousId: string | undefined;
  requestedId: string | undefined;
  openFailureId: string | null | undefined;
}): boolean {
  if (!input.requestedId || input.requestedId !== input.openFailureId) {
    return false;
  }

  return input.previousId !== input.requestedId;
}

export type SolutionOpenDialogPlan = 'close' | 'open' | 'stay-closed';

/**
 * What the failure dialog does when the latch or the dismiss flag changes.
 * A same-id retry keeps the failure record, so the dialog stays open without
 * a separate attempt check. No failure record means close, including while a
 * different solution's `selectBot` is in flight.
 */
export function planSolutionOpenDialog(input: {
  openFailureId: string | null | undefined;
  dismissed: boolean;
}): SolutionOpenDialogPlan {
  if (!input.openFailureId) {
    return 'close';
  }

  if (input.dismissed) {
    return 'stay-closed';
  }

  return 'open';
}

export type DismissedSelectionReset = 'leave' | 'forget' | 'reset';

/**
 * After the user dismisses the dialog, the solutions table must drop its
 * selection so the next click is a new selection. With no table, or with
 * nothing selected, only the remembered id is dropped.
 */
export function dismissedSelectionReset(input: {
  dismissed: boolean;
  tableReady: boolean;
  rowSelected: boolean;
}): DismissedSelectionReset {
  if (!input.dismissed) {
    return 'leave';
  }

  if (!input.tableReady || !input.rowSelected) {
    return 'forget';
  }

  return 'reset';
}

export type FailureDialogPlanEffect =
  | 'none'
  | 'close'
  | 'close-marked'
  | 'open';

/**
 * `close-marked` is a close this component requested. The later `close`
 * event must not be recorded as a user dismiss. `close` is the same close
 * when the dialog is already shut, so there is no event to mis-read.
 */
export function failureDialogPlanEffect(input: {
  openFailureId: string | null | undefined;
  dismissed: boolean;
  modalOpen: boolean;
}): FailureDialogPlanEffect {
  const plan = planSolutionOpenDialog({
    openFailureId: input.openFailureId,
    dismissed: input.dismissed,
  });

  if (plan === 'stay-closed') {
    return 'none';
  }

  if (plan === 'close') {
    return input.modalOpen ? 'close-marked' : 'close';
  }

  return 'open';
}

export function failureDialogCloseEffect(input: {
  programmatic: boolean;
  deleteHandoff: boolean;
}): { clearProgrammatic: boolean; dismiss: boolean; openDelete: boolean } {
  if (!input.programmatic) {
    return { clearProgrammatic: false, dismiss: true, openDelete: false };
  }

  return {
    clearProgrammatic: true,
    dismiss: false,
    openDelete: input.deleteHandoff,
  };
}

export function deleteDialogClosedEffect(input: {
  handoff: boolean;
  deleting: boolean;
  dismissed: boolean;
  failureHeld: boolean;
}): { clearHandoff: boolean; reopenFailure: boolean } {
  if (!input.handoff) {
    return { clearHandoff: false, reopenFailure: false };
  }

  return {
    clearHandoff: true,
    reopenFailure: !input.deleting && !input.dismissed && input.failureHeld,
  };
}

/** The failure dialog closes before the delete dialog opens, when it is open. */
export function deleteFromFailureDialog(
  modalOpen: boolean
): 'close-first' | 'open-now' {
  return modalOpen ? 'close-first' : 'open-now';
}

export function clearedSolutionOpenState(): {
  solutionOpenFailure: null;
  solutionOpenAttempt: null;
  solutionOpenFailureDismissed: false;
} {
  return {
    solutionOpenFailure: null,
    solutionOpenAttempt: null,
    solutionOpenFailureDismissed: false,
  };
}

/**
 * A `setParams` that names a different project drops the latch. The same
 * project, and a payload with no project id, leave it.
 */
export function solutionOpenLatchForProjectChange<T>(input: {
  currentProjectId: string | undefined;
  nextProjectId: string | undefined;
  failure: T | null | undefined;
  attempt: T | null | undefined;
  dismissed: boolean | undefined;
}): {
  solutionOpenFailure: T | null;
  solutionOpenAttempt: T | null;
  solutionOpenFailureDismissed: boolean;
} {
  const projectChanged =
    !!input.nextProjectId && input.nextProjectId !== input.currentProjectId;

  if (!projectChanged) {
    return {
      solutionOpenFailure: input.failure ?? null,
      solutionOpenAttempt: input.attempt ?? null,
      solutionOpenFailureDismissed: input.dismissed === true,
    };
  }

  return clearedSolutionOpenState();
}

/**
 * The id `deleteBot` sends. A completed open matches `selectedBot.displayName`
 * against the solutions list. A failed open does not assign `selectedBot`
 * (`setSelectedSolution` writes it in `onDoneAssignContext` only), so the list
 * is then matched by the stored failure id, then by `botName`.
 *
 * The failure dialog passes `preferOpenFailure` and deletes only the row
 * whose id is on that record. It does not fall through to the selected
 * solution: that would delete a different one when the failed id is absent.
 * The nav delete leaves the flag unset and still deletes the selected one.
 */
export function resolveDeleteSolutionTarget(input: {
  solutions: readonly SolutionListRow[] | undefined;
  selectedDisplayName: string | undefined;
  botName: string | undefined;
  openFailure: SolutionOpenFailure | null | undefined;
  preferOpenFailure?: boolean;
}): DeleteSolutionTarget | null {
  const selected = input.selectedDisplayName
    ? input.solutions?.find(
        (solution) => solution.displayName === input.selectedDisplayName
      )
    : undefined;
  const failed = input.openFailure
    ? input.solutions?.find(
        (solution) => solution.id === input.openFailure?.solutionId
      )
    : undefined;
  const named = input.botName
    ? input.solutions?.find((solution) => solution.name === input.botName)
    : undefined;

  if (input.preferOpenFailure) {
    if (!failed?.id) {
      return null;
    }

    const failedName =
      failed.displayName || input.openFailure?.displayName || failed.name;

    if (!failedName) {
      return null;
    }

    return {
      id: failed.id,
      displayName: failedName,
      solutionName: failed.name ?? undefined,
    };
  }

  const row = selected ?? failed ?? named;
  const displayName =
    row?.displayName ||
    (row && row === failed ? input.openFailure?.displayName : undefined) ||
    row?.name;

  if (!row?.id || !displayName) {
    return null;
  }

  return {
    id: row.id,
    displayName,
    solutionName: row.name ?? undefined,
  };
}
